<script setup async>
import {competitionFacts} from '~/utils/privateAccess';
const {outputDateFromDb} = useCrudConverters();
import CompetitionQuestionsPrompt from "@/components/competitions/CompetitionQuestionsPrompt.vue";
import CompetitionPdfDialog from '~/components/competitions/CompetitionPdfDialog.vue';
import {canExportCompetitionPdf, EXPORT_PDF_LABEL} from '~/composables/useCompetitionPdf';
import FormatExplainerDialog from '~/components/formats/FormatExplainerDialog.vue';
import {ref, computed, watch, onMounted, onBeforeUnmount} from "vue";
import OutstandingFeeBanner from "~/components/payments/OutstandingFeeBanner.vue";
import {useHeadService} from '~/composables/useHeadService';
import {useAuthService} from '~/composables/useAuthService';
import {useMessaging} from '~/composables/useMessaging';
import {useValidation} from "~/composables/useValidation";
import {useAmericanoFormat} from "~/composables/useAmericanoFormat";
import FrontendHeader from "~/components/layout/FrontendHeader.vue";
import ClubChip from "~/components/clubs/ClubChip.vue";
import AmericanoStandingsTable from "~/components/americanos/AmericanoStandingsTable.vue";
import AmericanoTableKeyDialog from "~/components/americanos/AmericanoTableKeyDialog.vue";
import AmericanoRoundList from "~/components/americanos/AmericanoRoundList.vue";
import NotPublishedNotice from "~/components/NotPublishedNotice.vue";
import ArchivedNotice from "~/components/ArchivedNotice.vue";
import JoinSettingsCard from "~/components/join/JoinSettingsCard.vue";
import PendingPlacementsCard from "~/components/placement/PendingPlacementsCard.vue";
import AwaitingPlacementBanner from "~/components/placement/AwaitingPlacementBanner.vue";
import JoinRequestsCard from "~/components/join/JoinRequestsCard.vue";
import JoinRequestPending from "~/components/join/JoinRequestPending.vue";
import InformationDialog from "~/components/InformationDialog.vue";
import ShortLinkShareWrapper from "~/components/share/ShortLinkShareWrapper.vue";
import {createError} from "nuxt/app";
import {useFormData} from "~/composables/useFormData";
import HandicapCalculatorButton from '~/components/handicaps/HandicapCalculatorButton.vue';
import {useHandicapSettingsFor} from '~/composables/useHandicapSettingsFor';

// Americano league member page (mirrors leagues/[leagueId]/[[cycleId]].vue):
// header + cycle switcher, then per selected division the individual standings
// and the whist round-by-round pairing sheet.
const {addListener, removeLocalListenersWithEvent} = useListenerService();
const {isGuid} = useValidation();
const {formatIcon} = useAmericanoFormat();
definePageMeta({
  name: 'AmericanoDetail',
  path: '/americanos/:americanoId/:cycleId?',
});

const {item: americano, getItem: getAmericano, itemPending: americanoLoading, hasItemErrors} = useCrudApi("api/v2/americanos");


const route = useRoute();
const router = useRouter();
const americanoId = ref(route.params.americanoId);
const cycleId = ref(route.params.cycleId);
const selectedCycleId = ref(cycleId.value);
const loadData = async () => {
  await getAmericano(americanoId.value);
  if (hasItemErrors.value) {
    throw createError({
      statusCode: 404,
      message: 'Americano league not found. Has it been deleted?'
    });
  }
  //selected cycle id must be within the cycles of the americano league
  if (selectedCycleId.value != null && americano.value.cycles.filter(_ => _.id === selectedCycleId.value).length === 0) {
    selectedCycleId.value = null;
  }
  //If no cycle is selected, select the active cycle
  if (selectedCycleId.value == null && americano.value.activeCycleId != null) {
    selectedCycleId.value = americano.value.activeCycleId;
  }
  // No active cycle → fall back to the most recent PUBLISHED cycle (by end date,
  // then start date) so the page shows a cycle rather than an empty selector.
  // Archived (deleted) cycles are never auto-selected.
  if (selectedCycleId.value == null) {
    const published = (americano.value.cycles ?? []).filter(c => c.published && !c.archivedAt);
    if (published.length) {
      const mostRecent = [...published].sort((a, b) =>
          (b.endDate || b.startDate || '').localeCompare(a.endDate || a.startDate || ''))[0];
      selectedCycleId.value = mostRecent.id;
    }
  }
};

await loadData();

const {setMeta} = useHeadService();
setMeta(
    americano.value?.clubName + ' / ' + americano.value?.name + ' on Tap Leagues',
    americano?.value?.clubName + ' / ' + americano?.value?.name + ' on Tap Leagues. Find out more about ' + americano?.value?.name + ' and view matches and results.'
);

const {formatRange} = useCrudConverters();
const {user, isAuthenticated, isAdminOfClub, isMemberOfClub} = useAuthService();
const {cycleItemTitle} = useFormData();

// The americano league itself has been deleted (archived).
const americanoArchived = computed(() => !!americano.value?.archivedAt);
usePrivatePage(computed(() => !!americano.value?.privateToViewer));

const publishedCycles = computed(() =>
    (americano.value?.cycles ?? []).filter(_ => _.published && !_.archivedAt));

// Slim cycle row from the americano's cycles list (dates, invite link, counts).
const selectedCycleSummary = computed(() =>
    americano.value?.cycles?.find(c => c.id === selectedCycleId.value) ?? null);

// Respect the cycle's published flag even on a direct /americanos/{id}/{cycleId} URL.
const selectedCycleUnpublished = computed(() => !!selectedCycleSummary.value && !selectedCycleSummary.value.published);

// A direct URL can point at a deleted (archived) cycle.
const selectedCycleArchived = computed(() => !!selectedCycleSummary.value?.archivedAt);

// ── Full cycle detail (divisions, standings rows, round-by-round matches) ──
const {item: cycle, getItem: getCycle, itemPending: cyclePending, hasItem: hasCycle} = useCrudApi('api/v2/americanoCycles');

// Handicap calculator in the action row: the league's settings (null = handicaps off) + every player in the cycle
const {settings: handicapSettings} = useHandicapSettingsFor(
    computed(() => americano.value?.id ? {americanoId: americano.value.id} : null),
    computed(() => americano.value?.handicapsEnabled === true));
const handicapEntries = computed(() => ((cycle.value?.divisions ?? []).flatMap((d) => d.players ?? []))
    .map((p) => ({teamId: p.teamId ?? p.playerId ?? p.id, playerId: p.playerId ?? p.id ?? null,
      name: p.fullName ?? p.name ?? [p.firstName, p.lastName].filter(Boolean).join(' '), value: p.handicap ?? null})));

const cycleViewable = computed(() =>
    isGuid(selectedCycleId.value) && !selectedCycleUnpublished.value && !selectedCycleArchived.value);

if (cycleViewable.value) {
  await getCycle(selectedCycleId.value);
}

// Divisions in sort order; withdrawn players stay in their standings rows.
const divisions = computed(() =>
    [...(cycle.value?.divisions ?? [])].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0)));

// Presentation-only: which division pill is active (null = show all)
const activeDivisionId = ref(null);
// Which division the mobile "Key" dialog is open for (null = closed).
const keyDivision = ref(null);

// "My matches" affordance: the signed-in player's standings rows and pairings
// get the brand tint wherever they appear.
const highlightPlayerIds = computed(() =>
    user.value?.playerId ? [user.value.playerId] : []);

// Is the signed-in player seated in this cycle?
const isInCycle = computed(() => {
  if (!isAuthenticated.value || !user.value?.playerId || !hasCycle.value) return false;
  const playerId = user.value.playerId;
  return (cycle.value?.divisions ?? []).some(d =>
      (d.players ?? []).some(p => p.playerId === playerId && p.withdrawnAt == null));
});
// Joined and waiting for a seat (JOIN_SETTINGS_PLAN.md §6).
const isAwaitingPlacement = computed(() => !!cycle.value?.currentUserPendingPlacement);
// The user's own pending join request for this cycle. Fetched client-side (the
// auth user object carries no americano cycle requests), so re-read per cycle.
const {isPending: hasPendingRequest, cancelling: cancelPending, cancelRequest, refresh: refreshPendingRequest} =
    useJoinRequestStatus('americanoCycle', computed(() => cycle.value?.id));
watch(() => cycle.value?.id, () => refreshPendingRequest());
// Divisions with their seated players — the place dialog swaps a waiting player in.
const placementDivisions = computed(() => (cycle.value?.divisions ?? []).map(d => ({
  id: d.id,
  name: d.name,
  players: (d.players ?? []).filter(p => p.withdrawnAt == null).map(p => ({id: p.playerId, fullName: p.fullName})),
})));

// Export PDF (the selected cycle's per-division standings + rounds): club
// admins and super admins only (owner decision, 7 Oct 2026).
const pdfOpen = ref(false);
const canExportPdf = computed(() => !!americano.value && canExportCompetitionPdf(user.value, americano.value.clubId));
const pdfName = computed(() => [americano.value?.name, cycle.value?.name].filter(Boolean).join(' '));

// ── entity chat (ladder-page pattern): seated players and club admins can
// start or join THIS CYCLE's group chat; participants track the seats. ──
const {entityChatLoading, createOrOpenEntityChat} = useMessaging();
const canAccessChat = computed(() =>
    isAuthenticated.value && hasCycle.value && (isInCycle.value || isAdminOfClub(americano.value?.clubId)));
const handleChatClick = async () => {
  await createOrOpenEntityChat(
      {americanoCycleId: cycle.value.id, conversationId: cycle.value.conversationId},
      () => getCycle(cycle.value.id),
  );
};

// Filter the cycle's join link through visibility rules (same as the league page)
const visibleJoinLink = computed(() => {
  const raw = selectedCycleSummary.value?.visibleInviteLink;
  if (!raw) return null;
  if (isAdminOfClub(americano.value?.clubId)) return raw;

  // Non-admins require: visible (0), active, and scope-allowed
  const scopeAllowed = raw.inviteScope !== 1 || isMemberOfClub(americano.value?.clubId);
  return raw.inviteEntityPageSetting !== 1 && raw.isActive && scopeAllowed
      ? raw : null;
});

const titleActions = computed(() => {
  const actions = [];

  // Top-right header keeps the admin shortcuts into the club admin pages,
  // matching the box league / ladder member pages.
  if (americano.value && isAdminOfClub(americano.value.clubId)) {
    actions.push({
      title: 'Manage cycles & divisions',
      icon: 'mdi-format-list-numbered',
      // Lands on the cycle in view, not whichever sorts first.
      click: () => router.push({
        name: 'ClubAmericanoManage',
        params: {clubId: americano.value.clubId, americanoId: americano.value.id},
        ...(isGuid(selectedCycleId.value) ? {query: {cycle: selectedCycleId.value}} : {}),
      }),
      color: 'success',
    });
    actions.push({
      title: 'Americano league settings',
      icon: 'mdi-pencil',
      click: () => router.push({
        name: 'ClubAmericanoSettings',
        params: {clubId: americano.value.clubId, americanoId: americano.value.id},
      }),
      color: 'info',
    });
  }

  return actions;
});

watch(selectedCycleId, async (val) => {
  router.replace({name: 'AmericanoDetail', params: {americanoId: americanoId.value, cycleId: val}});
  activeDivisionId.value = null;
  if (isGuid(val) && cycleViewable.value) {
    await getCycle(val);
  }
});

watch(() => cycleId.value, (val) => {
  selectedCycleId.value = val;
});

const reloadCycle = async () => {
  if (cycleViewable.value) await getCycle(selectedCycleId.value);
};

onMounted(() => {
  // A recorded/approved result changes standings and round rows.
  addListener('fixtureUpdated', reloadCycle);
  // Approving/rejecting a join request updates the cycle's accessApplicationCount.
  addListener('requestsUpdated', () => loadData());
});

onBeforeUnmount(() => {
  removeLocalListenersWithEvent('fixtureUpdated');
  removeLocalListenersWithEvent('requestsUpdated');
});
</script>

<template>
  <base-page>
    <template v-if="americanoLoading">
      <v-container class="large">
        <v-row>
          <v-col cols="12">
            <v-skeleton-loader height="50"/>
          </v-col>
          <v-col cols="12">
            <v-skeleton-loader type="list-item-avatar-two-line"/>
          </v-col>
          <v-col cols="12">
            <v-skeleton-loader height="50"/>
          </v-col>
          <v-col cols="12">
            <v-skeleton-loader type="table"/>
          </v-col>
        </v-row>
      </v-container>
    </template>
    <!-- Members only for this viewer (PRIVACY_PLAN.md §4.1): the stub keeps the name and the join link -->
    <template v-else-if="americano && americano.privateToViewer">
      <v-container class="large">
        <frontend-header :americano="americano" :club="americano.club ?? (americano.clubId ? {id: americano.clubId, slug: americano.clubId, name: americano.clubName} : null)" :title="americano.name" :title-icon="formatIcon" title-icon-color="americano">
          <template #subTitle>
            <club-chip v-if="americano.club" :club="americano.club"></club-chip>
            <span v-else-if="americano.clubName" class="tl-chip">{{ americano.clubName }}</span>
          </template>
        </frontend-header>
        <div class="tl-action-strip tl-action-strip--solo my-5">
          <short-link-share-wrapper>
            <template #default="{ click, loading: shareLoading }">
              <button class="as" @click="click" :disabled="shareLoading">
                <v-icon size="17" icon="mdi-share-variant"/>Share
              </button>
            </template>
          </short-link-share-wrapper>
        </div>
        <private-notice reason="competition" entity="Americano league" :name="americano.name" :club-name="americano.clubName" :join-slug="americano.visibleInviteLink?.slug"
                      :facts="competitionFacts('americano', americano, d => outputDateFromDb(d, 'd MMM yyyy'))"/>
      </v-container>
    </template>
    <template v-else-if="americano && americanoArchived">
      <!-- Deleted (archived) Americano league - show the notice instead of any content -->
      <v-container class="large">
        <archived-notice entity="Americano league" :name="americano.name"/>
      </v-container>
    </template>
    <template v-else-if="americano">
      <v-container class="large">
        <outstanding-fee-banner v-if="isAuthenticated && selectedCycleId" :source-id="selectedCycleId"/>
        <frontend-header
            :club="americano.club"
            :americano="americano"
            :title="americano.name"
            :title-icon="formatIcon"
            title-icon-color="americano"
            :actions="titleActions"
        >
          <template #subTitle>
            <club-chip :club="americano.club"></club-chip>
          </template>
        </frontend-header>
      </v-container>
      <v-container class="large">
        <!-- Direct link to a deleted (archived) cycle -->
        <archived-notice v-if="selectedCycleArchived" entity="cycle" :name="selectedCycleSummary.name"/>

        <!-- Direct link to an unpublished cycle isn't viewable publicly -->
        <not-published-notice v-else-if="selectedCycleUnpublished" entity="cycle"/>

        <template v-else>
          <v-alert v-if="publishedCycles.length === 0" color="info" variant="tonal"
                   icon="mdi-information" class="mb-4">
            This Americano league has no cycles
          </v-alert>
          <v-alert v-else-if="americano.activeCycleId == null" color="info" variant="tonal" icon="mdi-information" class="mb-4">
            This Americano league has no active cycles
          </v-alert>

          <template v-if="publishedCycles.length > 0">
            <div class="cycle-field mb-5">
              <div class="tl-label mb-1">
                {{ americano.activeCycleId != null ? 'Cycle' : 'Cycle (no active cycle, select one to view)' }}
              </div>
              <v-select
                  density="compact"
                  variant="outlined"
                  bg-color="white"
                  class="cycle-select"
                  :hide-details="true"
                  item-value="id"
                  :item-title="cycleItemTitle"
                  :items="publishedCycles"
                  v-model="selectedCycleId"
              >
              </v-select>
            </div>
            <template v-if="isGuid(selectedCycleId)">
              <!-- Admin invite CTA — per selected cycle; opens the join-links dialog -->
              <join-settings-card
                  v-if="isAdminOfClub(americano.clubId) && selectedCycleSummary"
                  :key="selectedCycleId"
                  class="mb-4"
                  :reference-type="6"
                  :reference-id="selectedCycleId"
                  :club-id="americano.clubId"
                  :entity-name="`${americano.name} / ${selectedCycleSummary.name}`"
                  @changed="loadData"
              />
              <pending-placements-card
                  v-if="isAdminOfClub(americano.clubId) && hasCycle"
                  :reference-type="6"
                  :reference-id="selectedCycleId"
                  :club-id="americano.clubId"
                  :players-per-team="1"
                  :count="cycle.pendingPlacementCount ?? selectedCycleSummary?.pendingPlacementCount ?? 0"
                  :entry-fee-minor="americano.entryFeeMinor ?? null"
                  :entry-fee-currency="americano.entryFeeCurrency ?? 'gbp'"
                  :divisions="placementDivisions"
                  :manage-route="{name: 'ClubAmericanoManage', params: {clubId: americano.clubId, americanoId: americano.id}, query: {cycle: selectedCycleId}}"
                  @changed="loadData"
              />
              <awaiting-placement-banner
                  v-if="isAwaitingPlacement"
                  :placement="cycle.currentUserPendingPlacement"
                  :entity-name="`${americano.name} / ${cycle.name}`"
                  @changed="loadData"
              />
              <!-- Pending join requests for the selected cycle (admins only) -->
              <join-requests-card
                  v-if="isAdminOfClub(americano.clubId) && selectedCycleSummary"
                  :count="selectedCycleSummary.accessApplicationCount ?? 0"
                  :filters="{ americanoCycleIds: [selectedCycleId] }"
              />

              <!-- Loading the cycle detail -->
              <template v-if="cyclePending && !hasCycle">
                <v-skeleton-loader class="mb-3" type="table"></v-skeleton-loader>
              </template>
              <template v-else-if="hasCycle">
                <!-- ======== ACTION STRIP ======== -->
                <div class="tl-action-strip mb-4">
                  <!-- Export PDF (club admins and super admins only) -->
                  <button v-if="canExportPdf" class="as" @click="pdfOpen = true">
                    <v-icon size="17" icon="mdi-file-pdf-box"/>
                    {{ EXPORT_PDF_LABEL }}
                  </button>

                  <!-- Share -->
                  <short-link-share-wrapper
                      :join-reference-type="6" :join-reference-id="selectedCycleId"
                      :can-share-join="isAdminOfClub(americano.clubId)">
                    <template #default="{ click, loading: shareLoading }">
                      <button class="as" @click="click" :disabled="shareLoading">
                        <v-icon size="17" icon="mdi-share-variant"/>
                        Share
                      </button>
                    </template>
                  </short-link-share-wrapper>

                  <!-- View matches -->
                  <router-link
                      class="as"
                      :to="{ name: 'FixtureSearch', query: { clubIds: [americano.clubId], americanoIds: [americano.id], americanoCycleIds: [selectedCycleId] } }"
                  >
                    <v-icon size="17" icon="mdi-scoreboard"/>
                    View matches
                  </router-link>

                  <!-- Stats -->
                  <router-link
                      class="as"
                      :to="{ name: 'AmericanoStats', params: { americanoId: americano.id } }"
                  >
                    <v-icon size="17" icon="mdi-chart-line"/>
                    Stats
                  </router-link>

                  <!-- Chat (ladder-page pattern) -->
                  <button v-if="canAccessChat" class="as" :disabled="entityChatLoading" @click="handleChatClick">
                    <v-icon size="17" icon="mdi-forum"/>{{ cycle?.conversationId ? 'Join Chat' : 'Start Chat' }}
                  </button>

                  <!-- Handicap calculator (handicaps on) -->
                  <handicap-calculator-button action pairs :admin="isAdminOfClub(americano.clubId)" :settings="handicapSettings" :entries="handicapEntries"
                                              :scoring-system-id="americano?.scoringSystemId ?? null" noun="player"/>

                  <!-- Information (admin-authored rich text) — only when content exists -->
                  <americano-table-key-dialog
                      :model-value="keyDivision != null"
                      :has-set-scores="keyDivision?.hasSetScores === true"
                      @update:model-value="keyDivision = null"
                  />
                  <format-explainer-dialog format="americano" :admin="isAdminOfClub(americano.clubId)" :payments="americano.entryFeeMinor != null"/>
                  <competition-pdf-dialog v-if="canExportPdf && cycle" v-model="pdfOpen" format="americanoCycle" :competition-id="cycle.id" :name="pdfName"/>
                  <information-dialog :html="americano.informationHtml"/>

                  <!-- Pending request (already asked to join this cycle) -->
                  <join-request-pending
                      v-if="!isInCycle && !isAwaitingPlacement && hasPendingRequest"
                      compact
                      :entity-name="cycle?.name ?? americano.name"
                      :cancelling="cancelPending"
                      @cancel="cancelRequest"
                  />

                  <!-- Join this cycle -->
                  <router-link
                      v-else-if="!isInCycle && !isAwaitingPlacement && visibleJoinLink"
                      class="as as--join"
                      :to="'/invite/' + visibleJoinLink.slug"
                  >
                    <v-icon size="17" icon="mdi-account-plus"/>
                    Join
                  </router-link>

                  <div class="vsep"></div>

                  <!-- Date range chip -->
                  <span
                      class="tl-chip as-chip"
                      :class="cycle.isActive ? 'tl-chip--ok' : ''"
                  >
                    <v-icon size="14" icon="mdi-calendar"/>
                    {{ formatRange(cycle.startDate, cycle.endDate) }}
                  </span>
                </div>

                <!-- ======== DIVISION PILLS (show all or jump to one) ======== -->
                <div v-if="divisions.length > 1" class="div-pills">
                  <button
                      class="pill"
                      :class="{ active: activeDivisionId === null }"
                      @click="activeDivisionId = null"
                  >All divisions
                  </button>
                  <button
                      v-for="division in divisions"
                      :key="division.id"
                      class="pill"
                      :class="{ active: activeDivisionId === division.id }"
                      @click="activeDivisionId = division.id"
                  >{{ division.name }}
                  </button>
                </div>

                <!-- Nothing seated yet: an info alert gave a member nothing to
                     act on and an admin nowhere to go. -->
                <competitions-empty-state
                    v-if="divisions.length === 0"
                    entity-type-name="americano"
                    noun="divisions"
                    admin-hint="Set up the divisions, then seat players in them. Anyone joining with the link waits until you place them."
                    icon="mdi-view-dashboard-outline"
                    :is-admin="isAdminOfClub(americano.clubId)"
                    :ended="!!cycle?.endDate && new Date(cycle.endDate) < new Date()">
                  <v-btn color="primary" variant="flat" rounded="pill" size="small"
                         :to="{name: 'ClubAmericanoManage', params: {clubId: americano.clubId, americanoId: americano.id}, query: {cycle: cycle?.id}}">
                    Set up divisions
                  </v-btn>
                </competitions-empty-state>

                <!-- ======== DIVISION CARDS: standings + the rotation sheet in one
                     container — side by side on large screens ======== -->
                <section
                    v-for="division in divisions"
                    :key="division.id"
                    :id="`division-${division.id}`"
                    class="division"
                    :class="{ 'division--hidden': activeDivisionId !== null && activeDivisionId !== division.id }"
                >
                  <div class="tl-card">
                    <div class="dhead">
                      <div class="dname">
                        {{ division.name }}
                        <!-- mobile-only: hover tooltips don't exist there, so the
                             column meanings live in a key dialog instead -->
                        <button type="button" class="dkey" @click="keyDivision = division">
                          <v-icon size="13" icon="mdi-help-circle-outline"/>
                          Key
                        </button>
                      </div>
                    </div>
                    <v-row no-gutters class="division-body">
                      <v-col cols="12" lg="6" class="pa-3 pt-3 division-standings">
                        <americano-standings-table
                            :americano-id="americano?.handicapsEnabled ? americano.id : null"
                            :scoring-system-id="americano?.scoringSystemId ?? null"
                            :has-set-scores="division.hasSetScores === true"
                            :players="division.players"
                            :highlight-player-ids="highlightPlayerIds"
                        />
                      </v-col>
                      <v-col cols="12" lg="6" class="pa-3 pt-3">
                        <div class="division-col-label">Matches</div>
                        <americano-round-list
                            :fixtures="division.fixtures || []"
                        />
                      </v-col>
                    </v-row>
                  </div>
                </section>
              </template>
            </template>
          </template>
        </template>
      </v-container>
    </template>
    <competition-questions-prompt competition-type="americano" :competition-id="americano?.id"/>
  </base-page>
</template>

<style scoped lang="scss">
/* cycle picker styled like the kit field (league page) */
.cycle-field {
  max-width: 420px;
}

.cycle-select :deep(.v-field) {
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
}

.cycle-select :deep(.v-field__outline__start),
.cycle-select :deep(.v-field__outline__end) {
  border-color: rgb(var(--v-theme-borderLight));
  opacity: 1;
}

.cycle-select :deep(.v-field__outline__start) {
  border-radius: 12px 0 0 12px;
}

.cycle-select :deep(.v-field__outline__end) {
  border-radius: 0 12px 12px 0;
}

/* division pills (league page .div-pills) */
.div-pills {
  display: flex;
  gap: 8px;
  margin: 4px 0 16px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.pill {
  font-size: 12.5px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 999px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  color: rgb(var(--v-theme-charcoal));
  white-space: nowrap;
  cursor: pointer;
  font-family: inherit;

  &.active {
    background: rgb(var(--v-theme-text));
    border-color: rgb(var(--v-theme-text));
    color: #fff;
  }
}

.division {
  margin-top: 18px;

  &:first-of-type {
    margin-top: 0;
  }

  &--hidden {
    display: none;
  }
}

/* division card header bar (league page .dhead) */
.dhead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 9px 10px 9px 16px;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

.dname {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13.5px;
  font-weight: 700;
  letter-spacing: -0.2px;
}

/* Mobile-only key link — desktop has hover tooltips on the column headers */
.dkey {
  display: none;
  align-items: center;
  gap: 3px;
  font-size: 11.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  cursor: pointer;

  @media (max-width: 599px) {
    display: inline-flex;
  }
}

.division-standings {
  @media (min-width: 1280px) {
    border-right: 1px solid rgb(var(--v-theme-borderLight));
  }
}

.division-col-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: rgb(var(--v-theme-textSubtle));
  padding: 0 10px 6px;
}
</style>
